import React, { useState, useEffect } from 'react';
import Button from "../ui/Button";

interface TermsOfServiceModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline?: () => void;
  viewOnly?: boolean;
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({
  isOpen,
  onAccept,
  onDecline,
  viewOnly = false
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [acceptButtonEnabled, setAcceptButtonEnabled] = useState(false);

  useEffect(() => {
    if (hasScrolledToBottom) {
      const timer = setTimeout(() => {
        setAcceptButtonEnabled(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasScrolledToBottom]);
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
      setAcceptButtonEnabled(viewOnly);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, viewOnly]);
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = element;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setHasScrolledToBottom(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-5">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 py-6 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-black mb-2">Terms of Service</h2>
          {viewOnly && <p className="text-sm text-neutral-600 md:text-xs max-sm:text-xs">
            Please read and accept our Terms of Service to continue
          </p>}
        </div>
        <div
          className="flex-1 overflow-y-auto max-h-[60vh] px-6 py-6"
          onScroll={handleScroll}
        >
          <div className="prose prose-sm max-w-none">
            <h1 className="text-xl font-semibold text-black text-center mb-6 md:text-lg max-sm:text-base">
              CADENCE IO TERMS OF SERVICE
            </h1>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              This application and all accompanying websites and pages (collectively called the "App") is
              operated by Cadence IO, LLC ("Cadence IO"). Throughout the App, the terms "we", "us" and
              "our" refer to Cadence IO. Cadence IO offers this App, including all information, tools and
              services available from this App to you, the user, conditioned upon your acceptance of all
              terms, conditions, policies and notices stated here.
            </p>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              By visiting our App and/or posting to our public forum, you engage in our "Service" and agree
              to be bound by these Terms of Service, including those additional terms and conditions and
              policies referenced herein and/or available by hyperlink, which are hereby incorporated by
              reference. These Terms of Service apply to all users of the App, including without limitation
              users who are browsers, vendors, customers, merchants, contributors of Content, and/or any
              company that manages a roster of artists and helps promote their releases.
            </p>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              Please read these Terms of Service carefully before accessing or using our App. By accessing
              or using any part of the App, you agree to be bound by these Terms of Service. If you do not
              agree to these Terms of Service, then you may not access the App or use any aspect of the
              Service. If these Terms of Service are considered an offer, acceptance is expressly limited
              to these Terms of Service.
            </p>
            <p className="mb-6 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              By agreeing to these Terms of Service, you represent and warrant that you have reached the age
              of majority in your jurisdiction of residence, have the authority to bind your organization to
              these terms, and you are otherwise authorized to use the App and its features.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 1 – ELIGIBILITY AND ACCESS
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              The Service is only available to verified businesses working in the music industry – primarily
              record labels and distributors. At this time, there is no option for general public sign-up or
              account creation in connection with the Service. Access is granted to you, the user, by way of a
              unique label access code only, supplied by Cadence IO upon User's creation of an account.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 2 - ACCURACY, COMPLETENESS AND TIMELINESS OF INFORMATION
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              We are not responsible if information made available on this App is not accurate, complete or
              current. The material on this App is provided for general information only and should not be
              relied upon or used as the sole basis for making decisions without consulting primary, more
              accurate, more complete or more timely sources of information.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 3 - MODIFICATIONS TO THE SERVICE
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              We reserve the right to modify or discontinue the Service (or any part or Cadence IO Content
              thereof) at any time, upon fourteen (14) days' advance written notice to you. As part of these
              modifications, we may change and/or remove any features of the Service at any time, provided
              that we give you fourteen (14) days' advance written notice.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 4 – ACCURACY OF ACCOUNT INFORMATION
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              You agree to provide current, complete and accurate account information for your use of the
              Service. You agree to promptly update your account and other information, including your email
              address, and you agree to keep your employee data accurate and up to date.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 5 - ERRORS, INACCURACIES AND OMISSIONS
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              Occasionally there may be information on our App or in the Service that contains typographical
              errors, inaccuracies or omissions that may relate to product descriptions, pricing, promotions,
              offers, or other such information. We reserve the right to correct any errors, inaccuracies or
              omissions, and to change or update information if any information in the Service or on any
              related website is inaccurate at any time without prior notice.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 6 – BILLING AND PAYMENT
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              You agree that your use of the Service shall incur a flat fee assessed per Pitch you engage
              Cadence IO to submit to DSPs for you. Upon execution of a SaaS Agreement and your agreement
              to these Terms of Service, Cadence IO will share the Client payment page with you.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 7 - OPTIONAL TOOLS
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              We may provide you with access to third-party tools over which we neither monitor nor have any
              control nor input. You acknowledge and agree that we provide access to such tools "as is" and
              "as available" without any warranties, representations or conditions of any kind.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 8 - THIRD-PARTY LINKS
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              Certain Cadence IO Content, products and services available through the Service may include
              materials from third-parties ("Third-Party Content"). Third-party links on the App may direct
              you to third-party websites that are not affiliated with us.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 9 - USER COMMENTS, FEEDBACK AND OTHER SUBMISSIONS
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              If, at our request, you send certain specific submissions, or if, without a request from us,
              you post to our feedback submission form, or send creative ideas, suggestions, proposals, plans,
              or other materials, you grant to Cadence IO a worldwide, perpetual, unrestricted, irrevocable,
              royalty-free license to edit, copy, reproduce, publish, upload, post, transmit, distribute,
              publicly display, perform, modify, create derivative works from, sublicense, and otherwise
              freely use, such comments without compensation or acknowledgment.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 10 - PERSONAL INFORMATION
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              Your mandatory submission of Personal Information, and User-Supplied Content through the Client
              account dashboard or otherwise on the App is governed by our Privacy Policy. For purposes of
              this Agreement, "Personal Information" shall mean User's email address(es) and DSP login
              credentials.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 11 - PROHIBITED USES
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              You are prohibited from using the App or its Content for any unlawful purpose; to solicit others
              to perform or participate in any unlawful acts; to violate any international, federal, provincial
              or state regulations, rules, laws, or local ordinances; to infringe upon or violate our
              intellectual property rights or the intellectual property rights of others.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 12 - DISCLAIMER OF WARRANTIES; LIMITATION OF LIABILITY
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              We do not guarantee, represent or warrant that your use of our Service will be uninterrupted,
              timely, secure or error-free. We do not guarantee any specific results will be obtained from
              our efforts to pitch editorial Content to DSPs.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 13 - INDEMNIFICATION
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              You agree to indemnify, defend and hold harmless Cadence IO and our subsidiaries, affiliates,
              partners, officers, directors, agents, contractors, licensors, service providers, subcontractors,
              suppliers, interns and employees, harmless from any claim or demand, including reasonable
              attorneys' fees, made by any third-party due to or arising out of your breach of these Terms
              of Service.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 14 – OWNERSHIP
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              The Service and App, including all associated intellectual property rights, are owned exclusively
              by Cadence IO, LLC. Most aspects of the Service and App, including, but not limited to, all
              methods, processes, Cadence IO Content, formats, domain names, and extensions are the exclusive
              property of Cadence IO.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 15 – COPYRIGHT INFRINGEMENT
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              Cadence IO prohibits copyright infringement on its App. Pursuant to the Digital Millennium
              Copyright Act ("DMCA"), 17 U.S.C. § 512, Cadence IO provides the following contact information
              for receipt of infringement notices: Cadence IO, LLC, DMCA Agent, admin@cadenceio.com.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 16 – TRADEMARK INFRINGEMENT
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              Cadence IO prohibits all forms of trademark infringement on its App. Cadence IO provides the
              following contact information for receipt of infringement notices: Cadence IO, LLC
              admin@cadenceio.com.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 17 - GOVERNING LAW
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              These Terms of Service, and your use of the App and the Service, shall be governed by and
              construed in accordance with the laws of the State of Colorado, without regard for any conflict
              of laws principles. You and Cadence IO hereby agree to use good faith efforts to resolve any
              disputes that arise under these Terms of Service through mandatory mediation.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 18 - GENERAL
            </h2>
            <p className="mb-4 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              These Terms of Service and any policies or operating rules posted by us on this App or in respect
              to the Service constitutes the entire agreement and understanding between you and us and govern
              your use of the Service, superseding any prior or contemporaneous agreements, communications and
              proposals, whether oral or written, between you and us.
            </p>
            <h2 className="text-lg font-semibold text-black mt-8 mb-4 pb-2 border-b border-neutral-200 md:text-base max-sm:text-sm">
              SECTION 19 - CONTACT INFORMATION
            </h2>
            <p className="mb-6 text-neutral-700 leading-relaxed md:text-sm max-sm:text-xs">
              Questions or any other notice or other communication regarding these Terms of Service should
              be sent to Cadence IO at admin@cadenceio.com.
            </p>
            <div className="bg-primary-50 p-4 rounded-lg border-l-4 border-primary-400 mt-8">
              <p className="text-sm font-medium text-primary-900 md:text-xs max-sm:text-xs">
                <strong>By clicking "Accept", you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong>
              </p>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 border-t border-neutral-200 bg-neutral-50 rounded-b-xl">
          <div className="text-center mb-4">
            {!hasScrolledToBottom && (
              <p className="text-primary-700 text-sm animate-pulse md:text-xs max-sm:text-xs">
                ↓ Please scroll to the bottom to continue
              </p>
            )}
          </div>

          <div className="flex-shrink-0 px-4 sm:px-6 py-4 sm:py-5 border-t border-neutral-200 bg-neutral-50 rounded-b-lg sm:rounded-b-xl">
            {!viewOnly ? (
              <div className="flex justify-center">
                <Button
                  type="button"
                  className="sm:px-6 sm:text-base"
                  onClick={onAccept}
                >
                  Close
                </Button>
              </div>
            ) : (
              <div className="flex  flex-row max-sm:flex-col justify-end gap-3">
                {onDecline && viewOnly && (
                  <Button
                    variant="neutral"
                    type="button"
                    onClick={onDecline}
                  >
                    Decline
                  </Button>
                )}
                <button
                  type="button"
                  className={`px-6 py-2 rounded-lg font-medium transition-all ${acceptButtonEnabled
                    ? 'bg-primary-700 text-white hover:bg-primary-800 cursor-pointer'
                    : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                    }`}
                  onClick={onAccept}
                  disabled={!acceptButtonEnabled}
                >
                  {acceptButtonEnabled ? 'Accept Terms' : 'Scroll to Enable'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServiceModal;